import { clsx } from 'clsx';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type CountPillColor = 'teal' | 'green' | 'orange' | 'red' | 'gray';

// Figma 14851:77932 / 77935 / 77999 + the GroupManagement Members chip pattern. Rounded-full
// pill with an icon + count, used across dashboard headers for "trainees in group", "runs
// completed", and similar metric chips. Backgrounds are mode-invariant tints (locked-light)
// paired with locked-saturated text + border — they sit on top of cards/panels which already
// handle their own mode flip, so the chip stays readable in both modes.
const colorChrome: Record<CountPillColor, string> = {
    teal: 'bg-teal-50 border-teal-200 text-teal-600',
    green: 'bg-green-50 border-green-500 text-green-700',
    orange: 'bg-orange-50 border-amber-500 text-amber-700',
    red: 'bg-red-50 border-red-500 text-red-700',
    gray: 'bg-neutral-100 border-neutral-300 text-neutral-500',
};

interface CountPillProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
    color: CountPillColor;
    text: string | number;
    icon?: ReactNode;
}

const CountPill = ({ color, text, icon, onClick, className, disabled, ...props }: CountPillProps) => {
    const isInteractive = !!onClick && !disabled;
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            tabIndex={isInteractive ? 0 : -1}
            aria-disabled={disabled || !onClick}
            className={clsx(
                'inline-flex items-center justify-center gap-1.5 overflow-hidden rounded-full border px-2.5 py-1.5',
                'text-base leading-6 font-semibold shadow-sm',
                isInteractive
                    ? 'cursor-pointer focus-visible:ring-2 focus-visible:ring-current focus-visible:ring-offset-2 focus-visible:outline-none'
                    : 'pointer-events-none cursor-default',
                colorChrome[color] ?? colorChrome.gray,
                className
            )}
            {...props}
        >
            {icon && <span className="inline-flex shrink-0 items-center">{icon}</span>}
            <span>{text}</span>
        </button>
    );
};

export default CountPill;
